import React, {useEffect, useRef, useState} from 'react';
import {Box, Card, CardContent, Grid} from "@mui/material";
import {Redirect} from "react-router-dom";
import DefaultContainer from "components/containers/DefaultContainer";
import {Authorizations, currentUserHasAuthorization} from "utils/authorizations";

import {list as shippingMethodsList} from "api/shippingMethod/list";
import showRetailer from "api/retailer/show";
import {fetchRetailerLineItems} from "actions/lineItem/retailerList";
import moment from "moment";
import RetailerPickingListHeader from "./components/RetailerPickingListHeader";
import {greyishBlue100} from "../../../assets/jss/main";
import ShippingMethodFilters from "./components/ShippingMethodFilters";
import ArticlesList from "./components/ArticlesList";
import StickyFooter from "./components/StickyFooter";
import TicketModal from "./components/TicketModal";
import {LineItemStatus} from "../../../utils/LineItemStatus";
import {confirmItem, confirmItemReturn, reserveItem} from "../../../actions/lineItem/update";
import {FormattedMessage} from "react-intl";
import ColoredToast from "../../../components/coloredToast/ColoredToast";

RetailerPickingList.propTypes = {};

function RetailerPickingList({ match }) {
    if (!currentUserHasAuthorization(Authorizations.GLOBAL_DEMO)) {
        return <Redirect to={`/picking/retailers/${match.params.id}/order/${match.params.params}`} />;
    }

    const [params] = useState(() =>
        Object.fromEntries(new URLSearchParams(decodeURIComponent(match.params.params)))
    );

    const [shippingMethods, setShippingMethods] = useState([]);
    const [selectedShippingMethods, setSelectedShippingMethods] = useState([])
    const [retailer, setRetailer] = useState(null);
    const [searchBarInput, setSearchBarInput] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: moment()
    });
    const [focusedInput, setFocusedInput] = useState(null);
    const [openScanModal, setOpenScanModal] = useState(false);
    const [openTicketModal, setOpenTicketModal] = useState(false)

    const [lineItems, setLineItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEmpty, setIsEmpty] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [openToast, setOpenToast] = useState({open: false, type: "success", label: ""})

    const itemsPerPage = 10;

    const searchInputRef = useRef(null);

    const isMac = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac');

    const fetchShippingMethods = () => {
        shippingMethodsList(`retailersIds=${match.params.id}`).then(res => {
            setShippingMethods(res);
        });
    };

    const fetchRetailer = () => {
        showRetailer(match.params.id).then(res => {
            setRetailer(res);
        });
    };

    const init = () => {
        fetchShippingMethods();
        fetchRetailer();
    };

    const handleDateChange = (startDate, endDate) => {
        // Empty for now, will be completed in future MR
    };

    const itemWorkflowUpdate = (item, updateArticleGlobally = false, shouldDisplayToast = true) => {
        if (LineItemStatus.PENDING === item.status) {
            return reserveItem(item).then((response) => {
                if (updateArticleGlobally) {
                    setLineItems(prev => prev.map((lineItem) => lineItem['@id'] === response['@id'] ? {...lineItem, status: response.status} : lineItem))
                }
                if (shouldDisplayToast) {
                    setOpenToast({
                        open: true,
                        type: "success",
                        label: <FormattedMessage id={"picking.retailer.itemWorkflowUpdate.reserved.toast.success"} values={{label: response.name}}/>
                    })
                }
                return response
            })
                .catch((error) => {
                    setOpenToast({
                        open: true,
                        type: "error",
                        label: <FormattedMessage id={"picking.retailer.itemWorkflowUpdate.toast.error"} />
                    })
                })
        }

        if (LineItemStatus.RESERVED === item.status) {
            return confirmItem(item).then((response) => {
                if (updateArticleGlobally) {
                    setLineItems(prev => prev.map((lineItem) => lineItem['@id'] === response['@id'] ? {...lineItem, status: response.status} : lineItem))
                }
                if (shouldDisplayToast) {
                    setOpenToast({
                        open: true,
                        type: "success",
                        label: <FormattedMessage id={"picking.retailer.itemWorkflowUpdate.prepared.toast.success"} values={{label: response.name}}/>
                    })
                }
                return response
            })
                .catch((error) => {
                    setOpenToast({
                        open: true,
                        type: "error",
                        label: <FormattedMessage id={"picking.retailer.itemWorkflowUpdate.toast.error"} />
                    })
                })
        }

        if (LineItemStatus.RETURNING_RETAILER === item.status) {
            return confirmItemReturn(item).then((response) => {
                if (updateArticleGlobally) {
                    setLineItems(prev => prev.map((lineItem) => lineItem['@id'] === response['@id'] ? {...lineItem, status: response.status} : lineItem))
                }
                if (shouldDisplayToast) {
                    setOpenToast({
                        open: true,
                        type: "success",
                        label: <FormattedMessage id={"picking.retailer.itemWorkflowUpdate.returned.toast.success"} values={{label: response.name}}/>
                    })
                }
                return response
            })
                .catch((error) => {
                    setOpenToast({
                        open: true,
                        type: "error",
                        label: <FormattedMessage id={"picking.retailer.itemWorkflowUpdate.toast.error"} />
                    })
                })
        }

        return Promise.resolve()
    }

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (retailer) {
            fetchRetailerLineItems(`retailer_id=${retailer.id}`).then((response) => {
                setTotalPages(Math.ceil(response["hydra:totalItems"] / itemsPerPage));
                setLineItems(response["hydra:member"]);
                if (response["hydra:member"].length === 0) {
                    setIsEmpty(true);
                }
                setLoading(false);
            });
        }
    }, [retailer]);

    return (
        <div>
            {openTicketModal && (
                <TicketModal open={openTicketModal} />
            )}
            {openToast.open && (
                <ColoredToast
                    open={openToast.open}
                    handleClose={() => setOpenToast({open: false, type: "success", label: ""})}
                    type={openToast.type}
                    label={openToast.label}
                    customTimeout={2000}
                />
            )}
            <DefaultContainer>
                <Box sx={{ pt: '24px' }}>
                    <Card  sx={{minHeight: '100%', boxShadow: "unset", border: `1px solid ${greyishBlue100}` }}>
                        <CardContent>
                            <Grid
                                container
                                direction={'column'}
                            >
                                <Grid item>
                                    <RetailerPickingListHeader
                                        retailer={retailer}
                                        isMac={isMac}
                                        searchInputRef={searchInputRef}
                                        onSearchChange={setSearchBarInput}
                                        dateRange={dateRange}
                                        onDatesChange={({startDate, endDate}) => setDateRange({startDate, endDate: endDate ?? moment()})}
                                        focusedInput={focusedInput}
                                        onFocusChange={setFocusedInput}
                                        onDateClose={handleDateChange(dateRange.startDate, dateRange.endDate)}
                                    />
                                </Grid>

                                <Grid item sx={{mt: "32px"}}>
                                    <ShippingMethodFilters
                                        shippingMethods={shippingMethods}
                                        selectedShippingMethods={selectedShippingMethods}
                                        setSelected={setSelectedShippingMethods}
                                    />
                                </Grid>

                                <Grid item sx={{mt: "32px"}}>
                                    <ArticlesList
                                        retailer={retailer}
                                        filters={{searchBarInput, dateRange, shippingMethods: selectedShippingMethods}}
                                        lineItems={lineItems}
                                        loading={loading}
                                        isEmpty={isEmpty}
                                        totalPages={totalPages}
                                        openScanModal={openScanModal}
                                        setOpenScanModal={setOpenScanModal}
                                        resetData={init}
                                        itemWorkflowUpdate={itemWorkflowUpdate}
                                    />
                                </Grid>
                            </Grid>

                        </CardContent>
                    </Card>
                </Box>
            </DefaultContainer>
            <StickyFooter openScanModal={setOpenScanModal}/>
        </div>

    );
}

export default RetailerPickingList;